import React, { useState, useRef, useEffect, useCallback } from 'react';
import './EPaperViewer.css';

let pdfJsLoaded = false;
function loadPdfJs() {
  return new Promise((resolve) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    if (pdfJsLoaded) {
      const iv = setInterval(() => { if (window.pdfjsLib) { clearInterval(iv); resolve(window.pdfjsLib); } }, 100);
      return;
    }
    pdfJsLoaded = true;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    document.head.appendChild(s);
  });
}

// HD multiplier: render at this many times the display size for crisp text
// 3x means even on a 1x screen text will be razor sharp
const HD = 3;

export default function EPaperViewer({ epaper }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [zoomLevel,   setZoomLevel]   = useState(1);
  const [fitCSSWidth, setFitCSSWidth] = useState(0); // display width in CSS px
  const [fullscreen,  setFullscreen]  = useState(false);
  const [pdfDoc,      setPdfDoc]      = useState(null);
  const [rendering,   setRendering]   = useState(false);
  const [loadError,   setLoadError]   = useState('');
  const [loadingPdf,  setLoadingPdf]  = useState(false);

  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isPanning  = useRef(false);
  const panStart   = useRef(null);
  const panOrigin  = useRef({ x: 0, y: 0 });

  const canvasRef    = useRef();
  const viewportRef  = useRef();
  const containerRef = useRef();
  const renderTask   = useRef(null);

  // ── How wide should the page display? (CSS px) ───────────────
  const computeFitCSSWidth = useCallback(async (doc, pageNum) => {
    if (!doc || !viewportRef.current) return 800;
    const page   = await doc.getPage(pageNum);
    const native = page.getViewport({ scale: 1 });
    // fill the viewport minus padding
    const available = viewportRef.current.clientWidth - 32;
    return Math.min(available, native.width * 2); // never smaller than available
  }, []);

  // ── Load PDF ─────────────────────────────────────────────────
  useEffect(() => {
    if (!epaper || epaper.fileType !== 'pdf') return;
    setLoadError(''); setLoadingPdf(true);
    setPdfDoc(null); setCurrentPage(1); setZoomLevel(1); setPanOffset({ x: 0, y: 0 });

    loadPdfJs().then(lib => {
      lib.getDocument({
        url: epaper.fileURL,
        cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
        cMapPacked: true,
      }).promise.then(async doc => {
        const w = await computeFitCSSWidth(doc, 1);
        setFitCSSWidth(w);
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setLoadingPdf(false);
      }).catch(err => {
        console.error(err);
        setLoadError('Could not load PDF. Try opening it directly.');
        setLoadingPdf(false);
      });
    });
  }, [epaper, computeFitCSSWidth]);

  // ── HD Render ────────────────────────────────────────────────
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current || !fitCSSWidth) return;
    if (renderTask.current) { renderTask.current.cancel(); renderTask.current = null; }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(currentPage);

      // CSS display size = fitCSSWidth * zoomLevel
      const displayW = fitCSSWidth * zoomLevel;

      // Native viewport at scale=1 gives us the PDF point dimensions
      const native = page.getViewport({ scale: 1 });

      // scale to fill displayW, then multiply by HD for sharpness
      const renderScale = (displayW / native.width) * HD;
      const vp = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current;
      const ctx    = canvas.getContext('2d');

      // Physical canvas pixels = HD * display pixels
      canvas.width  = vp.width;
      canvas.height = vp.height;

      // CSS size = display size (HD factor removed via CSS)
      canvas.style.width  = `${displayW}px`;
      canvas.style.height = `${(displayW / native.width) * native.height}px`;

      const task = page.render({ canvasContext: ctx, viewport: vp, intent: 'print' });
      renderTask.current = task;
      await task.promise;
      setRendering(false);
    } catch (e) {
      if (e?.name !== 'RenderingCancelledException') { console.error(e); setRendering(false); }
    }
  }, [pdfDoc, currentPage, zoomLevel, fitCSSWidth]);

  useEffect(() => { renderPage(); }, [renderPage]);

  // Reset pan on page change
  useEffect(() => { setPanOffset({ x: 0, y: 0 }); }, [currentPage]);

  // Recompute fit on resize
  useEffect(() => {
    if (!pdfDoc) return;
    const onResize = async () => {
      const w = await computeFitCSSWidth(pdfDoc, currentPage);
      setFitCSSWidth(w);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pdfDoc, currentPage, computeFitCSSWidth]);

  // ── Pan ──────────────────────────────────────────────────────
  const canPan = zoomLevel > 1;

  const onMouseDown = (e) => {
    if (!canPan) return;
    e.preventDefault();
    isPanning.current = true;
    panStart.current  = { x: e.clientX, y: e.clientY };
    panOrigin.current = { ...panOffset };
  };
  const onMouseMove = useCallback((e) => {
    if (!isPanning.current) return;
    setPanOffset({ x: panOrigin.current.x + e.clientX - panStart.current.x,
                   y: panOrigin.current.y + e.clientY - panStart.current.y });
  }, []);
  const onMouseUp = useCallback(() => { isPanning.current = false; }, []);

  const onTouchStart = (e) => {
    if (!canPan || e.touches.length !== 1) return;
    isPanning.current = true;
    panStart.current  = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    panOrigin.current = { ...panOffset };
  };
  const onTouchMove = useCallback((e) => {
    if (!isPanning.current || e.touches.length !== 1) return;
    e.preventDefault();
    setPanOffset({ x: panOrigin.current.x + e.touches[0].clientX - panStart.current.x,
                   y: panOrigin.current.y + e.touches[0].clientY - panStart.current.y });
  }, []);
  const onTouchEnd = useCallback(() => { isPanning.current = false; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend',  onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend',  onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  // ── Keyboard ─────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') setCurrentPage(p => Math.min(totalPages, p + 1));
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   setCurrentPage(p => Math.max(1, p - 1));
      if (e.key === '+' || e.key === '=') setZoomLevel(z => +Math.min(z + 0.25, 4).toFixed(2));
      if (e.key === '-')                  setZoomLevel(z => +Math.max(z - 0.25, 0.5).toFixed(2));
      if (e.key === '0')                  { setZoomLevel(1); setPanOffset({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [totalPages]);

  if (!epaper) return (
    <div className="viewer-empty">
      <div className="viewer-empty-icon">📰</div>
      <h3>No E-Paper Available</h3>
      <p>Select a date from the calendar to view the newspaper</p>
    </div>
  );

  const isPDF = epaper.fileType === 'pdf';
  const pages = isPDF ? [] : (epaper.pageURLs || []);
  const effectiveTotalPages = isPDF ? totalPages : pages.length;

  const handleFullscreen = () => {
    if (!fullscreen) { containerRef.current?.requestFullscreen?.(); setFullscreen(true); }
    else             { document.exitFullscreen?.();                  setFullscreen(false); }
  };

  const zoomIn  = () => setZoomLevel(z => +Math.min(z + 0.25, 4).toFixed(2));
  const zoomOut = () => setZoomLevel(z => {
    const n = +Math.max(z - 0.25, 0.5).toFixed(2);
    if (n <= 1) setPanOffset({ x: 0, y: 0 });
    return n;
  });
  const resetZoom = () => { setZoomLevel(1); setPanOffset({ x: 0, y: 0 }); };
  const prevPage  = () => setCurrentPage(p => Math.max(1, p - 1));
  const nextPage  = () => setCurrentPage(p => Math.min(effectiveTotalPages, p + 1));

  return (
    <div className={`epaper-viewer ${fullscreen ? 'fullscreen' : ''}`} ref={containerRef}>

      {/* Toolbar */}
      <div className="viewer-toolbar">
        <div className="toolbar-left">
          <span className="viewer-date">📅 {epaper.date}</span>
        </div>
        <div className="toolbar-center">
          <button className="tool-btn" onClick={prevPage} disabled={currentPage === 1 || loadingPdf}>◀ Prev</button>
          <span className="page-indicator">Page {currentPage} of {effectiveTotalPages}</span>
          <button className="tool-btn" onClick={nextPage} disabled={currentPage === effectiveTotalPages || loadingPdf}>Next ▶</button>
        </div>
        <div className="toolbar-right">
          <button className="tool-btn" onClick={zoomOut} disabled={zoomLevel <= 0.5}>🔍−</button>
          <button className="tool-btn zoom-reset" onClick={resetZoom}>{Math.round(zoomLevel * 100)}%</button>
          <button className="tool-btn" onClick={zoomIn}  disabled={zoomLevel >= 4}>🔍+</button>
          <button className="tool-btn" onClick={handleFullscreen}>{fullscreen ? '⤡' : '⤢'}</button>
          {isPDF && (
            <a href={epaper.fileURL} target="_blank" rel="noopener noreferrer"
              className="tool-btn" style={{ textDecoration: 'none' }}>↗ Open</a>
          )}
        </div>
      </div>

      {canPan && <div className="pan-hint">✋ Click and drag to pan</div>}

      {/* Viewport */}
      <div
        className="viewer-viewport"
        ref={viewportRef}
        style={{ cursor: canPan ? (isPanning.current ? 'grabbing' : 'grab') : 'default' }}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {isPDF ? (
          <>
            {loadingPdf && (
              <div className="pdf-loading-overlay">
                <div className="pdf-spinner" /><p>Loading PDF…</p>
              </div>
            )}
            {loadError ? (
              <div className="viewer-error">
                <div style={{ fontSize: '3rem' }}>⚠️</div>
                <p>{loadError}</p>
                <a href={epaper.fileURL} target="_blank" rel="noopener noreferrer">Open PDF directly ↗</a>
              </div>
            ) : (
              <div
                className="canvas-wrapper"
                style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px)` }}
              >
                {rendering && !loadingPdf && (
                  <div className="render-overlay"><div className="pdf-spinner" /></div>
                )}
                <canvas ref={canvasRef} className="pdf-canvas" />
              </div>
            )}
          </>
        ) : (
          pages[currentPage - 1]
            ? <img src={pages[currentPage - 1]} alt={`Page ${currentPage}`} className="viewer-page-img" />
            : <div className="viewer-error"><p>Page not found</p></div>
        )}
      </div>

      {/* Page strip */}
      {effectiveTotalPages > 1 && (
        <div className="viewer-pages-strip">
          {Array.from({ length: effectiveTotalPages }, (_, i) => (
            <button key={i}
              className={`strip-btn ${currentPage === i + 1 ? 'active' : ''}`}
              onClick={() => setCurrentPage(i + 1)} disabled={loadingPdf}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}